import { useEffect, useMemo, useState } from "react";
import Column from "../../../../components/Column";
import { RawMaterialModel } from "../../../../../infrastructure/model/RawMaterialModel";
import { getListRawMaterialUseCase } from "../../../../../applications/useCase/ApiUseCase/RawMaterialApiUseCase";
import usePageState from "../../../../hooks/usePageState";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import { Button, Card, Divider } from "antd";
import Row from "../../../../components/Row";
import AppInputDate from "../../../../components/AppInputDate";
import useForm from "../../../../hooks/useForm";
import { CreateInventoryAuditRequest } from "../../../../../infrastructure/model/InventoryAuditModel";
import * as Yup from "yup";
import AppInputText from "../../../../components/AppInputText/AppInputText";
import AppInputNumber from "../../../../components/AppInputNumber";
import { createInventoryAuditUseCase } from "../../../../../applications/useCase/ApiUseCase/InventoryAuditApiUseCase";

interface InventoryAuditFormProps {
  setActiveTab: (key: string) => void;
}

function InventoryAuditForm(props: InventoryAuditFormProps) {
  const { setActiveTab } = props;

  const [listRaw, setListRaw] = useState<RawMaterialModel[]>([]);
  const { setLoading, showError, showSuccess, showClientError } = usePageState();

  async function getAllRawData() {
    const dataRes = await getListRawMaterialUseCase({
      setLoading,
      showError,
      showSuccess,
    });

    setListRaw(dataRes);

    const detailItems = dataRes?.map((e) => ({
      raw_id: e.id,
      quantity: 0,
      unit_price: 0,
    }));

    formik.updateFieldValue("details", detailItems);
  }

  const createInventoryAuditSchema = Yup.object({
    audit_date: Yup.number()
      .typeError("Ngày kiểm kê không hợp lệ")
      .required("Ngày kiểm kê là bắt buộc"),

    note: Yup.string().trim().required("Ghi chú là bắt buộc"),

    details: Yup.array()
      .of(
        Yup.object({
          raw_id: Yup.number()
            .typeError("Nguyên liệu không hợp lệ")
            .required("Nguyên liệu là bắt buộc"),

          quantity: Yup.number()
            .typeError("Số lượng phải là số")
            .required("Số lượng là bắt buộc")
            .min(0, "Số lượng không hợp lệ"),

          unit_price: Yup.number()
            .typeError("Đơn giá phải là số")
            .required("Đơn giá là bắt buộc")
            .min(0, "Đơn giá không hợp lệ"),
        }),
      )
      .required()
      .min(1),
  });

  const formik = useForm<CreateInventoryAuditRequest>({
    initialValues: {
      audit_date: 0,
      note: "",
      details: [],
    },
    validationSchema: createInventoryAuditSchema,
    onSubmit: async (values) => {
      const res = await createInventoryAuditUseCase({
        request: values,
        setLoading,
        showError,
        showSuccess,
      });

      if (res?.id) {
        formik.resetForm();
        setActiveTab("1");
      }
    },
  });

  const totalValue = useMemo(() => {
    return formik.values.details?.reduce((sum, t) => sum + t.quantity * t.unit_price, 0) || 0;
  }, [formik.values.details]);

  function updateQuantity(index: number, value: number) {
    const details = [...formik.values.details];
    details[index].quantity = value || 0;
    formik.updateFieldValue("details", details);
  }

  function updatePrice(index: number, value: number) {
    const details = [...formik.values.details];
    details[index].unit_price = value || 0;
    formik.updateFieldValue("details", details);
  }

  function formatCurrency(value: number) {
    return value.toLocaleString("vi-VN");
  }

  useAsyncEffect(async () => {
    await getAllRawData();
  }, []);

  return (
    <Column className="w-100 h-100">
      <Card title="Tạo phiếu kiểm kê">
        {/* FORM HEADER */}

        <Row className="gap-3 w-100">
          <Column className="col-6">
            <AppInputDate
              currentValue={formik.values.audit_date}
              orientation="col"
              label="Ngày kiểm kê"
              isRequired
              onChangeValue={(e) => formik.updateFieldValue("audit_date", e!)}
              errorText={formik.getFormErrorMessage("audit_date")}
            />
          </Column>

          <Column className="col-12">
            <AppInputText
              currentValue={formik.values.note}
              orientation="col"
              label="Ghi chú"
              isRequired
              additionProps={{
                placeholder: "Ví dụ: Phiếu kiểm kê ngày 15/03/2026",
              }}
              onTextChange={(e) => formik.updateFieldValue("note", e)}
              errorText={formik.getFormErrorMessage("note")}
            />
          </Column>

          <Button
            type="primary"
            style={{ marginTop: 26 }}
            onClick={() => formik.checkErrorAndSubmit(showClientError)}
          >
            Lưu
          </Button>
        </Row>

        <Divider />

        {/* TABLE HEADER */}

        <Row
          className="w-100 gap-2"
          style={{
            fontWeight: 600,
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: 8,
          }}
        >
          <Column className="col-1">#</Column>
          <Column className="col-7">Nguyên liệu</Column>
          <Column className="col-5">Số lượng</Column>
          <Column className="col-5">Đơn giá</Column>
          <Column className="col-5" style={{ textAlign: "end" }}>
            Giá trị
          </Column>
        </Row>

        {/* DETAIL ROW */}

        <Column className="gap-2 mt-2">
          {formik.values.details?.map((raw, index) => {
            const rawEntity = listRaw?.find((r) => r.id === raw.raw_id);

            const rowTotal = raw.quantity * raw.unit_price;

            return (
              <Row
                key={raw.raw_id}
                className="w-100 gap-2"
                style={{
                  alignItems: "center",
                  padding: "6px 0",
                }}
              >
                <Column className="col-1">{index + 1}</Column>

                <Column className="col-7">{rawEntity?.name}</Column>

                <Column className="col-5">
                  <AppInputNumber
                    value={raw.quantity}
                    onChangeValue={(v) => updateQuantity(index, v)}
                    suffix={rawEntity?.uom.name}
                  />
                </Column>

                <Column className="col-5">
                  <AppInputNumber
                    value={raw.unit_price}
                    onChangeValue={(v) => updatePrice(index, v)}
                    suffix="VNĐ"
                  />
                </Column>

                <Column className="col-5 " style={{ textAlign: "end" }}>
                  {formatCurrency(rowTotal)} VNĐ
                </Column>
              </Row>
            );
          })}
        </Column>

        <Divider />

        {/* TOTAL */}

        <Row
          style={{
            justifyContent: "flex-end",
            fontWeight: 600,
            fontSize: 16,
          }}
        >
          Tổng giá trị tồn: {formatCurrency(totalValue)} VNĐ
        </Row>
      </Card>
    </Column>
  );
}

export default InventoryAuditForm;
